import React from "react";

import { ListItemText, ListItemIcon, MenuItem, Tooltip } from "@material-ui/core";
import { BarChart, People, Dns, CreditCard } from "@material-ui/icons";
import classNames from "classnames";

import { IOrg } from "@schema";
import { googleGsuiteIcon } from "@src/assets/img/google";

import { AdminPanelViews } from "../AdminPanel.container";
import { useStyles } from "./AdminPanelSidebar.styles";
import { H2 } from "@components/ui";
import { getOrgDomainFromEmail, getOrgOverview } from "../AdminPanel.util";
import { track, TrackingEvents } from "@clockwise/web-commons/src/util/analytics.util";
import Sidebar from "@clockwise/web-commons/src/ui/layouts/Sidebar";

interface IAdminPanelSidebarProps {
  org: IOrg;
  currentView: AdminPanelViews;
  onSelectView: (view: AdminPanelViews) => void;
}

const iconByView = {
  Overview: <BarChart />,
  Members: <People />,
  PlansAndBilling: <CreditCard />,
  Settings: <Dns />,
};

export const AdminPanelSidebar = ({ org, currentView, onSelectView }: IAdminPanelSidebarProps) => {
  const classes = useStyles();
  const orgOverview = getOrgOverview(org);
  const orgDomain = getOrgDomainFromEmail(org.primaryOrgCalendar) || org.domains[0];

  const handleMenuItemClick = (selectedView: AdminPanelViews) => {
    onSelectView(selectedView);
  };

  const maybeRenderOrgOverview = () => {
    if (!orgOverview) {
      return null;
    }

    return (
      <MenuItem
        onClick={() => {
          track(TrackingEvents.ADMIN_PANEL.SIDEBAR_CLICK_ORG_OVERVIEW);
          handleMenuItemClick(AdminPanelViews.Overview);
        }}
        className={classNames([
          classes.menuItem,
          { [classes.menuItem__active]: currentView === AdminPanelViews.Overview },
        ])}
      >
        <Tooltip title={AdminPanelViews.Overview} placement={"right"}>
          <ListItemIcon className={classes.menuItemIcon}>
            {iconByView[AdminPanelViews.Overview]}
          </ListItemIcon>
        </Tooltip>
        <ListItemText inset primary={AdminPanelViews.Overview} />
      </MenuItem>
    );
  };

  const renderMembers = () => {
    return (
      <MenuItem
        onClick={() => {
          track(TrackingEvents.ADMIN_PANEL.SIDEBAR_CLICK_MEMBERS);
          handleMenuItemClick(AdminPanelViews.Members);
        }}
        className={classNames([
          classes.menuItem,
          { [classes.menuItem__active]: currentView === AdminPanelViews.Members },
        ])}
      >
        <Tooltip title={AdminPanelViews.Members} placement={"right"}>
          <ListItemIcon className={classes.menuItemIcon}>
            {iconByView[AdminPanelViews.Members]}
          </ListItemIcon>
        </Tooltip>
        <ListItemText inset primary={AdminPanelViews.Members} />
      </MenuItem>
    );
  };

  const renderOrgSettings = () => {
    return (
      <MenuItem
        onClick={() => {
          track(TrackingEvents.ADMIN_PANEL.SIDEBAR_CLICK_SETTINGS);
          handleMenuItemClick(AdminPanelViews.Settings);
        }}
        className={classNames([
          classes.menuItem,
          { [classes.menuItem__active]: currentView === AdminPanelViews.Settings },
        ])}
      >
        <Tooltip title={"Organization Settings"} placement={"right"}>
          <ListItemIcon className={classes.menuItemIcon}>
            {iconByView[AdminPanelViews.Settings]}
          </ListItemIcon>
        </Tooltip>
        <ListItemText inset primary={"Organization Settings"} />
      </MenuItem>
    );
  };

  const renderPlansAndBilling = () => {
    return (
      <MenuItem
        onClick={() => {
          track(TrackingEvents.ADMIN_PANEL.SIDEBAR_CLICK_PLANS_AND_BILLING);
          handleMenuItemClick(AdminPanelViews.PlansAndBilling);
        }}
        className={classNames([
          classes.menuItem,
          { [classes.menuItem__active]: currentView === AdminPanelViews.PlansAndBilling },
        ])}
      >
        <Tooltip title={"Plans and Billing"} placement={"right"}>
          <ListItemIcon className={classes.menuItemIcon}>
            {iconByView[AdminPanelViews.PlansAndBilling]}
          </ListItemIcon>
        </Tooltip>
        <ListItemText inset primary="Plans and Billing" />
      </MenuItem>
    );
  };

  const header = (
    <div className={classes.header}>
      <img src={googleGsuiteIcon} />
      <H2>{orgDomain}</H2>
    </div>
  );

  return (
    <Sidebar
      menuItems={[
        maybeRenderOrgOverview(),
        renderMembers(),
        renderPlansAndBilling(),
        renderOrgSettings(),
      ]}
      header={header}
    />
  );
};
